import { AfterViewChecked, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BlogService, BlogArticle } from './blog.service';
import { SeoService } from '../services/seo.service';

@Component({
  selector: 'app-blog-detail',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="blog-detail-container">
      <!-- Breadcrumbs -->
      @if (article) {
        <nav class="breadcrumbs">
          <ol itemscope itemtype="https://schema.org/BreadcrumbList">
            <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
              <a itemprop="item" href="/">
                <span itemprop="name">Domů</span>
              </a>
              <meta itemprop="position" content="1" />
            </li>
            <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
              <a itemprop="item" href="/blog">
                <span itemprop="name">Blog</span>
              </a>
              <meta itemprop="position" content="2" />
            </li>
            <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
              <span itemprop="name">{{ article.title }}</span>
              <meta itemprop="position" content="3" />
            </li>
          </ol>
        </nav>
      }
    
      @if (article) {
        <div class="blog-detail">
          <article itemscope itemtype="https://schema.org/Article">
            <img [src]="article.image" [alt]="article.title" class="article-image" (error)="onImageError($event)" itemprop="image" loading="lazy" decoding="async" />
            <div class="article-header">
              <h1 itemprop="headline">{{ article.title }}</h1>
              <div class="article-meta">
                <time [attr.datetime]="getISODate(article.date)" itemprop="datePublished">{{ article.date }}</time>
                <span class="author" itemprop="author" itemscope itemtype="https://schema.org/Person">
                  <span itemprop="name">Autor: {{ article.author }}</span>
                </span>
              </div>
              @if (article.tags && article.tags.length > 0) {
                <div class="tags-container">
                  @for (tag of article.tags; track tag) {
                    <span class="tag" itemprop="keywords">{{ tag }}</span>
                  }
                </div>
              }
            </div>
            <div
              #articleContent
              class="article-content"
              [ngClass]="'article-id-' + article.id"
              [innerHTML]="article.content"
              itemprop="articleBody"
              (click)="onArticleContentClick($event)"
            ></div>
          </article>
        </div>
      }

      @if (lightboxSrc) {
        <div
          class="image-lightbox"
          role="dialog"
          aria-modal="true"
          [attr.aria-label]="lightboxAlt || 'Zvětšený obrázek'"
          (click)="closeLightbox()"
        >
          <button
            type="button"
            class="image-lightbox-close"
            aria-label="Zavřít"
            (click)="closeLightbox($event)"
          >×</button>
          <img
            [src]="lightboxSrc"
            [alt]="lightboxAlt"
            class="image-lightbox-img"
            (click)="$event.stopPropagation()"
          />
        </div>
      }
    
      @if (!article && !loading) {
        <div class="not-found">
          <h2>Článek nenalezen</h2>
          <p>Omlouváme se, ale požadovaný článek nebyl nalezen.</p>
          <button (click)="goBack()">Zpět na seznam</button>
        </div>
      }
    
      @if (loading) {
        <div class="loading">
          <p>Načítání...</p>
        </div>
      }
    </div>
    `,
  styles: [`
    .blog-detail-container {
      max-width: 900px;
      margin: 0 auto;
      padding: 2rem 1rem;
    }

    .breadcrumbs {
      margin-bottom: 1.5rem;
      font-size: 0.9rem;
    }

    .breadcrumbs ol {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    }

    .breadcrumbs li {
      display: flex;
      align-items: center;
    }

    .breadcrumbs li:not(:last-child)::after {
      content: '›';
      margin: 0 0.5rem;
      color: #666;
    }

    .breadcrumbs a {
      color: #007bff;
      text-decoration: none;
      transition: color 0.3s;
    }

    .breadcrumbs a:hover {
      color: #0056b3;
      text-decoration: underline;
    }

    .breadcrumbs span {
      color: #666;
    }

.back-button {
  background: white;
  color: #007bff;
  border: solid 1px  #007bff; 
  padding: 0.75rem 1.5rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: background 0.3s;
  align-self: flex-start;
  margin-bottom: 20px;

  &:hover {
  color: white;
  background-color: #007bff;
  }
}

    article {
      background: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .article-image {
      width: 100%;
      height: 400px;
      object-fit: cover;
      border-radius: 8px;
      box-shadow: 0 4px 8px rgba(0,0,0,0.1);
      transition: transform 0.3s ease;
    }

    .article-image:hover {
      transform: scale(1.02);
    }

    .article-header {
      padding: 2rem;
      border-bottom: 1px solid #eee;
    }

    .article-header h1 {
      margin: 0 0 1rem 0;
      color: #333;
      font-size: 2.5rem;
    }

    .article-meta {
      display: flex;
      gap: 2rem;
      color: #666;
      font-size: 0.95rem;
      margin-bottom: 1rem;
    }

    .tags-container {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 1rem;
    }

    .tag {
      display: inline-block;
      background: var(--color-primary);
      color: white;
      padding: 6px 12px;
      border-radius: 4px;
      font-size: 0.85rem;
      font-weight: 500;
      transition: background 0.3s;
    }

    .tag:hover {
      background: #0056b3;
    }

    .article-content {
      padding: 2rem;
      line-height: 1.8;
      color: #444;
    }

    .article-content .align-center {
      display: block;
      margin-right: auto;
      margin-left: auto;
      max-width: 100%;
      box-sizing: border-box;
    }

    .image-lightbox {
      position: fixed;
      inset: 0;
      z-index: 1050;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.5rem;
      background: rgba(0, 0, 0, 0.88);
      cursor: zoom-out;
    }

    .image-lightbox-img {
      max-width: min(95vw, 1400px);
      max-height: 92vh;
      width: auto;
      height: auto;
      object-fit: contain;
      border-radius: 4px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      cursor: default;
    }

    .image-lightbox-close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      width: 2.5rem;
      height: 2.5rem;
      border: none;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.15);
      color: white;
      font-size: 1.75rem;
      line-height: 1;
      cursor: pointer;
      transition: background 0.2s;
    }

    .image-lightbox-close:hover {
      background: rgba(255, 255, 255, 0.3);
    }

    .article-content :deep(h2) {
      color: #333;
      margin-top: 2rem;
      margin-bottom: 1rem;
    }

    .article-content :deep(h3),
    .article-content :deep(h4) {
      color: #555;
      margin-top: 1.5rem;
      margin-bottom: 0.75rem;
    }

    .article-content :deep(p) {
      margin-bottom: 1rem;
    }

    .not-found, .loading {
      text-align: center;
      padding: 4rem 2rem;
    }

    .not-found h2 {
      color: #333;
      margin-bottom: 1rem;
    }

    .not-found button {
      background: #007bff;
      color: white;
      border: none;
      padding: 0.75rem 1.5rem;
      border-radius: 5px;
      cursor: pointer;
      font-size: 1rem;
      margin-top: 1rem;
    }

    .not-found button:hover {
      background: #0056b3;
    }

    @media (max-width: 768px) {
      .article-header h1 {
        font-size: 1.8rem;
      }

      .article-image {
        height: 250px;
      }

      .article-header, .article-content {
        padding: 1.5rem;
      }
    }
  `]
})
export class BlogDetailComponent implements OnInit, AfterViewChecked {
  @ViewChild('articleContent') articleContent?: ElementRef<HTMLElement>;

  article: BlogArticle | undefined;
  previousArticle: BlogArticle | undefined;
  nextArticle: BlogArticle | undefined;
  loading = true;
  lightboxSrc: string | null = null;
  lightboxAlt = '';
  private enhancedArticleId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private blogService: BlogService,
    private seo: SeoService
  ) {}

  ngOnInit(): void {
    // Get article from resolver (server-side) or load it (client-side)
    const resolvedArticle = this.route.snapshot.data['article'];
    const slug = this.route.snapshot.paramMap.get('slug');
    
    console.log('BlogDetailComponent - resolved article:', resolvedArticle);
    console.log('BlogDetailComponent - slug:', slug);
    
    if (resolvedArticle) {
      // Article was loaded by resolver (server-side)
      this.article = resolvedArticle;
      // Only update SEO on client-side
      if (typeof window !== 'undefined') {
        this.updateSEO();
      }
      this.loadRelatedArticles(slug!);
      this.loading = false;
    } else if (slug) {
      // Fallback: load article client-side if resolver didn't work
      console.log('BlogDetailComponent - loading article client-side with slug:', slug);
      this.blogService.getArticleBySlug(slug).subscribe({
        next: (article) => { 
          console.log('BlogDetailComponent - article loaded:', article);
          if (article) {
            this.article = article;
            this.updateSEO();
            this.loadRelatedArticles(slug);
          } else {
            console.warn('BlogDetailComponent - article not found for slug:', slug);
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('BlogDetailComponent - error loading article:', error);
          this.loading = false;
        }
      });
    } else { 
      console.log('BlogDetailComponent - no slug provided');
      this.loading = false;
    }
  }

  ngAfterViewChecked(): void {
    this.enhanceContentImages();
  }

  private enhanceContentImages(): void {
    if (typeof window === 'undefined' || !this.article || this.loading) {
      return;
    }

    if (this.enhancedArticleId === this.article.id) {
      return;
    }

    const container = this.articleContent?.nativeElement;
    if (!container) {
      return;
    }

    const images = container.querySelectorAll('img');
    if (images.length === 0) {
      return;
    }

    images.forEach((img) => {
      const element = img as HTMLImageElement;
      element.classList.add('article-lightbox-trigger');
      element.style.cursor = 'zoom-in';
      if (!element.title) {
        element.title = 'Klikněte pro zvětšení';
      }
    });

    this.enhancedArticleId = this.article.id;
  }

  private updateSEO(): void {
    if (this.article) {
      // Convert date to ISO format for structured data
      const publishedDate = this.getISODate(this.article.date);
      
      this.seo.updateTags({
        title: this.article.title,
        description: this.article.summary,
        keywords: this.article.tags.join(', '),
        author: this.article.author,
        image: this.article.image,
        url: `/blog/${this.article.slug}`,
        type: 'article',
        publishedTime: publishedDate,
        modifiedTime: publishedDate,
        section: 'Blog',
        tags: this.article.tags
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/blog']);
  }

  onArticleContentClick(event: MouseEvent): void {
    if (typeof window === 'undefined') {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLImageElement)) {
      return;
    }

    this.lightboxSrc = target.currentSrc || target.src;
    this.lightboxAlt = target.alt;
    document.body.style.overflow = 'hidden';
  }

  closeLightbox(event?: Event): void {
    event?.stopPropagation();
    this.lightboxSrc = null;
    this.lightboxAlt = '';
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.lightboxSrc) {
      this.closeLightbox();
    }
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    target.src = '/assets/img/logo.svg';
  }

  getISODate(dateString: string): string {
    try {
      // Convert Czech date format (DD.MM.YYYY) to ISO format
      const parts = dateString.split('.');
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        const isoDate = `${year}-${month}-${day}`;
        const date = new Date(isoDate);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          console.warn('Invalid date format:', dateString);
          return new Date().toISOString();
        }
        
        return date.toISOString();
      }
      
      // Fallback to direct parsing
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', dateString);
        return new Date().toISOString();
      }
      
      return date.toISOString();
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return new Date().toISOString();
    }
  }

  private loadRelatedArticles(currentSlug: string): void {
    // Load previous article
    this.blogService.getPreviousArticle(currentSlug).subscribe(article => {
      this.previousArticle = article;
      this.updateRelLinks();
    });

    // Load next article
    this.blogService.getNextArticle(currentSlug).subscribe(article => {
      this.nextArticle = article;
      this.updateRelLinks();
    });
  }

  private updateRelLinks(): void {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      return; // Skip on server-side
    }

    // Remove existing rel links
    const existingPrev = document.querySelector('link[rel="prev"]');
    const existingNext = document.querySelector('link[rel="next"]');
    
    if (existingPrev) existingPrev.remove();
    if (existingNext) existingNext.remove();

    // Add prev link
    if (this.previousArticle) {
      const prevLink = document.createElement('link');
      prevLink.setAttribute('rel', 'prev');
      prevLink.setAttribute('href', `/blog/${this.previousArticle.slug}`);
      document.head.appendChild(prevLink);
    }

    // Add next link
    if (this.nextArticle) {
      const nextLink = document.createElement('link');
      nextLink.setAttribute('rel', 'next');
      nextLink.setAttribute('href', `/blog/${this.nextArticle.slug}`);
      document.head.appendChild(nextLink);
    }
  }
}

